import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import saveAs from "file-saver";


const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export enum BillStatus {
  PENDING,
  GENERATED,
  CONFIRMED,
  ERROR,
}

export interface VendorInvoiceCost {
  id: number;
  count: number;
  totalCost: number;
  vendorConnectionProfileId: number[];
  vendorInvoiceId: string;
}

export interface ProviderBillType {
  id: number;
  type: string;
  dueDate: Date;
  startDate: Date;
  endDate: Date;
  profileId: string | string[];
  status: BillStatus;
  confirmedAmount: number;
  amount: number;
  vendorInvoiceCost: VendorInvoiceCost[];
  createdDate: Date;
  invoiceDate: Date;  
  excludeZero: boolean;
  ref: string;
  updatedBy?: string;
  historyTimestamp: Date;
  createdBy: string;
  confirmedBy: string;
}

export interface ProviderBillHistory {
  id: string;
  snapshot: ProviderBillType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  confirmedBy: string;
}


export async function fetchProviderBill(
  params: Params<ProviderBillType>
): Promise<IPageable<ProviderBillType>> {
  try {
    const response = await axiosInstance.get("/invoices/vendors", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}
export async function fetchProviderBillById(
  id: ProviderBillType["id"]
): Promise<ProviderBillType> {
  try {
    const response = await axiosInstance.get(`/invoices/vendors/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting invoices vendors/${id}.`
    );
  }
}

export async function fetchConfirmedProviderBills(params: {
  page: number;
  size: number;
}): Promise<{ content: { id: string; customerName: string }[] }> {
  try {
    const response = await axiosInstance.get("/invoices/vendors", {
      params: { ...params, status: "CONFIRMED" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching provider bills:", error);
    throw error;
  }
}

export interface BulkGenerateRequest {
  connectionProfileIds: number[];
  periods: { month: number; year: number }[];
}

export interface BulkGenerateResult {
  month: number;
  year: number;
  invoiceId: number;
  invoiceRef: string;
  success: boolean;
  errorMessage?: string;
}

export interface BulkGenerateResponse {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: BulkGenerateResult[];
}

export const createProviderBill = async (
  params: BulkGenerateRequest
): Promise<BulkGenerateResponse> => {
  try {
    const response = await axiosInstance.post(`/invoices/vendors/bulk-generate`, params);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.errorMessage ||
        "An error occurred while generating provider bills."
    );
  }
};

export const confirmApi = async (
  id: string | number,
  amount: number,
  ref: string
): Promise<ProviderBillType> => {
  try {
    const response = await axiosInstance.post(
      `/invoices/vendors/${id}/confirm`,
      null,
      {
        params: { amount, ref },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while confirming the provider bill."
    );
  }
};

export const fetchAccountsByProfile = async (profileId: string) => {
  try {
    const response = await axiosInstance.get(
      `/connection-profiles/vendors?profileId=${profileId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching accounts for profile:", error);
    throw error;
  }
};

export const useProviderBillQuery = (
  params: Params<ProviderBillType>
): UseQueryResult<IPageable<ProviderBillType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["providerBill", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchProviderBill(effectiveParams),
    // Poll every 2 seconds while any invoice has PENDING status
    refetchInterval: (query) => {
      const data = (query as any)?.state?.data as IPageable<ProviderBillType> | undefined;
      const hasPending = data?.content?.some(
        (item) => item.status === BillStatus.PENDING || item.status === ("PENDING" as any)
      );
      return hasPending ? 2000 : false;
    },
  });
};

export const useProviderBillById = (
  id: ProviderBillType["id"]
): UseQueryResult<ProviderBillType, Error> => {
  return useQuery<ProviderBillType, Error>({
    queryKey: ["providerBillById", id],
    queryFn: () => fetchProviderBillById(id),
  });
};

export const exportProviderBil = async (
  params: Params<ProviderBillType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/invoices/vendors/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "Provider Bill.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching vendor:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchProviderBillHistory(
  id: ProviderBillType["id"],
  params: Params<ProviderBillType> = {}
): Promise<IPageable<ProviderBillType>> {
  try {
    const response = await axiosInstance.get(`/billings/history`, { params: { mainId: id , sort: "historyTimestamp,desc" , ...params } });
    const data = response.data;
return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Provider Bill history for ID ${id}.`
    );
  }
}

export const pdfProviderApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/invoices/vendors/${id}/pdf`, {
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = `invoice-${id}.pdf`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, filename);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};

export const pdfProviderDetailsApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(
      `/invoices/vendors/${id}/pdf/details`,
      {
        responseType: "blob",
      }
    );

    const disposition = response.headers["content-disposition"];
    let filename = `invoice-${id}.pdf`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, filename);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};

export const deleteProviderBills = async (
  ids: string[]
): Promise<void> => {
  try {
    const response = await axiosInstance.delete(`/invoices/vendors`, { data: ids });
    // Check if the response contains an error message (some APIs return 200 with error in body)
    if (response.data?.error || response.data?.message) {
      throw new Error(response.data.message || response.data.error);
    }
    console.log(`Provider Bills with IDs ${ids} deleted successfully.`);
  } catch (error: any) {
    // Extract error message from axios error response or use the error message directly
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An error occurred while deleting the provider bills.";
    throw new Error(errorMessage);
  }
};